import React, { Component } from "react";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import {
  Plus, FileText, CheckCircle2, Mail, MessageSquare, Send, Briefcase, UsersRound,
  Bell, ArrowUpRight,
} from "lucide-react";

import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";
import { logoutUser } from "../../actions/authActions";
import { toggleDarkMode } from "../../actions/preferenceActions";

import LatestArticles from "./widgets/LatestArticles";
import LatestNewsletters from "./widgets/LatestNewsletters";
import LatestContacts from "./widgets/LatestContacts";
import LatestAnalytics from "./widgets/LatestAnalytics";

import AdminCommandPalette from "./AdminCommandPalette";
import AdminNotifications from "./AdminNotifications";

import {
  AdminAppShell,
  PageHeader,
  Card,
  Stat,
  Skeleton,
  Breadcrumb,
} from "../../design-system";
import { buildAdminBreadcrumb } from "../../utils/adminBreadcrumb";

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stats: {
        articles:      { total: 0, published: 0, scheduled: 0, drafts: 0 },
        stages:        { total: 0 },
        professionals: { total: 0 },
        newsletters:   { total: 0, active: 0 },
        contacts:      { total: 0, sent: 0, pending: 0, failed: 0 },
      },
      loading: true,
    };
  }

  componentDidMount() {
    this.loadStats();
  }

  loadStats = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.get("/api/admin/stats");
      if (response.data.success) {
        this.setState({ stats: response.data.stats, loading: false });
      } else {
        throw new Error("Erreur lors du chargement des statistiques");
      }
    } catch (error) {
      console.error("Error loading stats:", error);
      Store.addNotification({
        title: "Erreur",
        message: "Impossible de charger les statistiques",
        type: "danger",
        insert: "top",
        container: "top-right",
        dismiss: { duration: 3000 },
      });
      this.setState({ loading: false });
    }
  };

  onLogout = (e) => {
    if (e) e.preventDefault();
    confirmAlert({
      title: "Déconnexion",
      message: "Êtes-vous sûr de vouloir vous déconnecter ?",
      type: "warning",
      confirmText: "Déconnexion",
      cancelText: "Annuler",
      onConfirm: () => {
        this.props.logoutUser();
        this.props.history.push("/login");
      },
    });
  };

  onToggleDarkMode = () => {
    const { _preferences } = this.props;
    this.props.toggleDarkMode(!_preferences?._dark);
  };

  render() {
    const { user } = this.props.auth;
    const { stats, loading } = this.state;
    const { _preferences, location } = this.props;

    return (
      <AdminAppShell
        user={user}
        currentPath={location.pathname}
        isDarkMode={_preferences?._dark || false}
        onToggleDarkMode={this.onToggleDarkMode}
        onLogout={this.onLogout}
        LinkComponent={Link}
        pageTitle="Tableau de bord"
        breadcrumb={<Breadcrumb items={buildAdminBreadcrumb(location.pathname)} LinkComponent={Link} />}
        counts={{ articles: stats.articles.total, contacts: stats.contacts.pending }}
        topbarCenter={<AdminCommandPalette />}
        topbarRight={<AdminNotifications />}
      >
        <div className="w-full px-5 sm:px-6 lg:px-8 xl:px-10 py-6 lg:py-8 space-y-6 lg:space-y-7">
          <PageHeader
            size="hero"
            eyebrow="— Tableau de bord"
            title={
              <>
                Bonjour, <span className="font-serif italic font-normal align-baseline">
                  {(user?.name || user?.email || "Karim").split(" ")[0]}.
                </span>
              </>
            }
            description="Vue d'ensemble de ton portfolio. Articles, abonnés newsletter, contacts récents — agrégés en un seul écran."
          />

          {/* Stats */}
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}><Skeleton className="h-16" /></Card>
              ))}
            </div>
          ) : (
            <Stat.Group>
              <Stat
                variant="ink"
                label="Articles"
                value={String(stats.articles.total)}
                hint={`${stats.articles.published} publiés${stats.articles.scheduled ? ` · ${stats.articles.scheduled} programmés` : ""} · ${stats.articles.drafts} brouillons`}
                icon={<FileText className="w-4 h-4" strokeWidth={1.8} />}
              />
              <Stat
                variant="cream"
                label="Publiés"
                value={String(stats.articles.published)}
                hint={stats.articles.drafts > 0 ? `${stats.articles.drafts} en attente` : "tout est à jour"}
                icon={<CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />}
              />
              <Stat
                variant="cream"
                label="Newsletter"
                value={String(stats.newsletters.active)}
                hint={`abonnés actifs / ${stats.newsletters.total} total`}
                icon={<Mail className="w-4 h-4" strokeWidth={1.8} />}
              />
              <Stat
                variant="ink"
                label="Contacts"
                value={String(stats.contacts.total)}
                hint={`${stats.contacts.sent} envoyés · ${stats.contacts.pending} en attente`}
                icon={<MessageSquare className="w-4 h-4" strokeWidth={1.8} />}
              />
            </Stat.Group>
          )}

          {/* Widget Analytics — pleine largeur (chart + top articles) */}
          <Card padding="none" className="overflow-hidden">
            <LatestAnalytics />
          </Card>

          {/* Widgets : derniers articles / newsletters / contacts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
            <Card padding="none" className="overflow-hidden">
              <LatestArticles />
            </Card>
            <Card padding="none" className="overflow-hidden">
              <LatestNewsletters />
            </Card>
            <Card padding="none" className="overflow-hidden">
              <LatestContacts />
            </Card>
          </div>

          {/* Actions rapides */}
          <section>
            <h2 className="text-xl font-bold tracking-tight mb-4">Actions rapides</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <QuickAction
                to="/dashboard/articles/new"
                icon={<FileText className="w-5 h-5" strokeWidth={1.6} />}
                title="Nouvel article"
                description="Brouillon avec titre et résumé"
              />
              <QuickAction
                to="/dashboard/stages/new"
                icon={<Briefcase className="w-5 h-5" strokeWidth={1.6} />}
                title="Nouveau stage"
                description="Ajoute une expérience"
              />
              <QuickAction
                to="/dashboard/professionals/new"
                icon={<UsersRound className="w-5 h-5" strokeWidth={1.6} />}
                title="Nouveau professionnel"
                description="Information professionnelle"
              />
            </div>
          </section>
        </div>
      </AdminAppShell>
    );
  }
}

function QuickAction({ to, icon, title, description }) {
  return (
    <Card as={Link} to={to} isHoverable className="group block no-underline text-text">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-bg-2 group-hover:bg-ink group-hover:text-cream flex items-center justify-center shrink-0 transition-colors">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-bold tracking-tight">{title}</h3>
            <ArrowUpRight className="w-3.5 h-3.5 text-mute group-hover:text-text transition-colors" strokeWidth={1.8} />
          </div>
          <p className="text-xs text-mute mt-0.5 leading-relaxed">{description}</p>
        </div>
      </div>
    </Card>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  _preferences: state._preferences,
});

export default connect(mapStateToProps, { logoutUser, toggleDarkMode })(withRouter(Dashboard));
